/**
 * Sequence order — drag the steps of something she knows into order.
 *
 * `correctOrder` is a permutation of the step ids, checked as one. Steps are
 * stored in their natural order for editing; the renderer shuffles from
 * `shuffleSeed`, so the same spec always deals the same starting jumble.
 */

import { z } from 'zod'
import { EmojiSchema, IdSchema, LabelSchema, SeedSchema, SentenceSchema, isUnique, specFor } from '../common'

export const SequenceStepSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  /** Spoken description for a child who cannot read the label yet. */
  altText: SentenceSchema.optional(),
})

export const SequenceOrderConfigSchema = z
  .object({
    /** What is being sequenced: "Growing a sunflower". */
    storyTitle: LabelSchema,
    steps: z.array(SequenceStepSchema).min(3).max(8),
    /** Step ids, first to last. */
    correctOrder: z.array(IdSchema).min(3).max(8),
    shuffleSeed: SeedSchema.default(1),
    presentation: z.enum(['horizontal', 'vertical', 'grid']).default('horizontal'),
    showNumbers: z.boolean().default(false),
    /** "Tell me what happens first" — sequencing carries the retelling too. */
    narrationPrompt: SentenceSchema.optional(),
  })
  .superRefine((config, ctx) => {
    const ids = config.steps.map((step) => step.id)
    if (!isUnique(ids)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: 'step ids must be unique' })
      return
    }
    if (!isUnique(config.correctOrder)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctOrder'], message: 'a step is listed twice' })
      return
    }
    if (config.correctOrder.length !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correctOrder'],
        message: `the order lists ${config.correctOrder.length} steps but there are ${ids.length}`,
      })
      return
    }
    const unknown = config.correctOrder.filter((id) => !ids.includes(id))
    if (unknown.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correctOrder'],
        message: `unknown step ${unknown.map((id) => `"${id}"`).join(', ')}`,
      })
    }
  })

export type SequenceOrderConfig = z.infer<typeof SequenceOrderConfigSchema>

export const SequenceOrderSpecSchema = specFor('sequence_order', SequenceOrderConfigSchema)
